/**
 * 車輛交班頁面
 * 輸入交班時的車輛油量和里程
 */

import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLiff } from '@/contexts/LiffContext'
import { getDriver } from '@/services/driverService'
import { updateVehicleState, getCurrentDuty } from '@/services/vehicleService'
import type { Driver, Vehicle, VehicleState } from '@/types'
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation'
import SpeedIcon from '@mui/icons-material/Speed'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

const VehicleHandoverPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { lineUser } = useLiff()

  // 從 location.state 取得資料
  const stateDriver = location.state?.driver as Driver | undefined
  const stateVehicle = location.state?.vehicle as Vehicle | undefined
  const stateVehicleState = location.state?.vehicleState as VehicleState | undefined

  // ========== State ==========
  const [driver, setDriver] = useState<Driver | null>(stateDriver || null)
  const [vehicle, setVehicle] = useState<Vehicle | null>(stateVehicle || null)
  const [startVehicleState, setStartVehicleState] = useState<VehicleState | null>(
    stateVehicleState || null
  )
  const [fuelLevel, setFuelLevel] = useState<string>('')
  const [mileage, setMileage] = useState<string>('')
  const [fuelAdded, setFuelAdded] = useState<string>('') // 本日加油量
  // 只有驥達（GIGO）的車要填油量／里程；其餘公司的車不填、帶 0、不檢核（2026-09 客戶決議）。
  // 來源：router state 帶進來的車輛（vehicles 清單）或 /attendances/current；舊後端沒此欄位視為要填。
  const [fuelMileageRequired, setFuelMileageRequired] = useState<boolean>(stateVehicle?.fuelMileageRequired !== false)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)
  const [countdown, setCountdown] = useState(3) // 倒數 3 秒
  const [noDutyDialogOpen, setNoDutyDialogOpen] = useState(false) // 無班可交提示
  const [noDutyCountdown, setNoDutyCountdown] = useState(3) // 無班可交倒數
  const [reportDate, setReportDate] = useState<string | null>(null) // 班別日期
  const [taskBlockedDialogOpen, setTaskBlockedDialogOpen] = useState(false) // 任務阻擋交班提示
  const [taskBlockedCountdown, setTaskBlockedCountdown] = useState(3) // 任務阻擋倒數
  const [taskBlockedMessage, setTaskBlockedMessage] = useState<string>('') // 阻擋訊息

  // 倒數結束 / 按「確認」共用的後續動作（點確認＝提前完成倒數後要做的事）
  const goToDailyReport = useCallback(() => {
    navigate('/daily-report', {
      replace: true,
      state: {
        driver,
        vehicle,
        vehicleState: startVehicleState,
        autoQuery: true,
        reportDate,  // 傳入班別日期，用於跨日班別正確查詢
      },
    })
  }, [navigate, driver, vehicle, startVehicleState, reportDate])

  const goToHome = useCallback(() => {
    navigate('/home', { replace: true })
  }, [navigate])

  const goToTaskList = useCallback(() => {
    navigate('/tasks', {
      replace: true,
      state: {
        driver: stateDriver,
        vehicle: stateVehicle,
        vehicleState: stateVehicleState,
      },
    })
  }, [navigate, stateDriver, stateVehicle, stateVehicleState])

  // ========== 倒數計時跳轉（交班成功） ==========
  useEffect(() => {
    if (successDialogOpen && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (successDialogOpen && countdown === 0) {
      goToDailyReport()
    }
  }, [successDialogOpen, countdown, goToDailyReport])

  // ========== 倒數計時跳轉（無班可交） ==========
  useEffect(() => {
    if (noDutyDialogOpen && noDutyCountdown > 0) {
      const timer = setTimeout(() => {
        setNoDutyCountdown(noDutyCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (noDutyDialogOpen && noDutyCountdown === 0) {
      goToHome()
    }
  }, [noDutyDialogOpen, noDutyCountdown, goToHome])

  // ========== 倒數計時跳轉（任務阻擋交班） ==========
  useEffect(() => {
    if (taskBlockedDialogOpen && taskBlockedCountdown > 0) {
      const timer = setTimeout(() => {
        setTaskBlockedCountdown(taskBlockedCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (taskBlockedDialogOpen && taskBlockedCountdown === 0) {
      goToTaskList()
    }
  }, [taskBlockedDialogOpen, taskBlockedCountdown, goToTaskList])

  // ========== 載入資料 ==========
  useEffect(() => {
    const loadData = async () => {
      if (!lineUser) return

      try {
        // 確認司機資料
        const driverData = await getDriver(lineUser.userId)
        if (!driverData) {
          navigate('/binding', { replace: true })
          return
        }

        // 如果有 state 資料，優先使用
        if (stateDriver && stateVehicle && stateVehicleState) {
          setDriver(stateDriver)
          setVehicle(stateVehicle)
          setStartVehicleState(stateVehicleState)
        } else {
          // 沒有 state 資料時，嘗試從 API 取得當前出勤資訊
          setDriver(driverData)

          const currentDuty = await getCurrentDuty()
          if (currentDuty) {
            setFuelMileageRequired(currentDuty.fuelMileageRequired !== false)
            // 有出勤記錄，設定車輛資訊
            setVehicle({
              vehicleId: currentDuty.vehicleId,
              vehicleNo: currentDuty.vehicleNo,
              vehicleType: currentDuty.vehicleType,
              vehicleTypeName: currentDuty.vehicleTypeName,
              status: 'IN_USE',
              statusName: '使用中',
              currentDriverId: driverData.driverId,
              currentDriverName: driverData.driverName,
            })
            setStartVehicleState({
              vehicleId: currentDuty.vehicleId,
              vehicleNo: currentDuty.vehicleNo,
              isEnabled: true,
              usageState: 'IN_USE',
              currentDriver: {
                driverId: driverData.driverId,
                driverName: driverData.driverName,
              },
              lastReportTime: currentDuty.clockInTime,
              fuelLevel: currentDuty.onDutyFuel,
              mileage: currentDuty.onDutyMileage,
            })
          } else {
            // 沒有出勤記錄，顯示提示並倒數後回首頁
            setNoDutyDialogOpen(true)
            return
          }
        }

        // 不預設帶入數值，讓司機手動輸入
        setFuelLevel('')
        setMileage('')
        setFuelAdded('')
      } catch (error) {
        console.error('載入資料失敗:', error)
        setError('載入資料失敗，請稍後再試')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [lineUser, stateDriver, stateVehicle, stateVehicleState, navigate])

  // ========== Event Handlers ==========
  const handleConfirm = async () => {
    if (!driver || !vehicle) return

    // 驗證輸入（不需填的車直接帶 0，並跳過所有油量／里程檢核）
    const fuelValue = fuelMileageRequired ? parseFloat(fuelLevel) : 0
    const mileageValue = fuelMileageRequired ? parseFloat(mileage) : 0

    if (fuelMileageRequired && (isNaN(fuelValue) || fuelValue < 0 || fuelValue > 500)) {
      setError('油量必須在 0-500 公升之間')
      return
    }

    if (fuelMileageRequired && (isNaN(mileageValue) || mileageValue < 0)) {
      setError('里程數必須大於等於 0')
      return
    }

    // 檢查里程是否合理（不能小於接班里程）
    if (fuelMileageRequired && startVehicleState && startVehicleState.mileage !== undefined && mileageValue < startVehicleState.mileage) {
      setError('交班里程不能小於接班里程')
      return
    }

    // 檢查耗油量不可為負（交班油量不能大於接班油量+加油量）
    const addedFuelValue = fuelMileageRequired ? (parseFloat(fuelAdded) || 0) : 0
    if (fuelMileageRequired && startVehicleState) {
      const startFuel = startVehicleState.fuelLevel ?? 0
      const fuelConsumed = startFuel + addedFuelValue - fuelValue
      if (fuelConsumed < 0) {
        setError(`耗油量不可小於 0\n（耗油量 = 接班油量 ${startFuel} + 加油量 ${addedFuelValue} − 交班油量 ${fuelValue} = ${fuelConsumed.toFixed(1)}）`)
        return
      }
    }

    setSubmitting(true)
    setError(null)

    try {
      // 更新車輛狀態
      const addedFuel = addedFuelValue
      const result = await updateVehicleState(
        vehicle.vehicleId,
        fuelValue,
        mileageValue,
        driver.driverId,
        addedFuel
      )

      if (result.success) {
        // 交班成功，儲存班別日期並顯示成功對話框
        if (result.reportDate) {
          setReportDate(result.reportDate)
        }
        setSuccessDialogOpen(true)
        setCountdown(3) // 重置倒數為 3 秒
      } else if (result.statusCode === 'LNE-0210') {
        // 有未完成任務阻擋交班，顯示提示後導回任務列表
        setTaskBlockedMessage(result.message)
        setTaskBlockedCountdown(3) // 重置倒數為 3 秒
        setTaskBlockedDialogOpen(true)
      } else {
        setError(result.message)
      }
    } catch (err) {
      console.error('交班失敗:', err)
      setError('交班失敗，請稍後再試')
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    navigate('/tasks', {
      state: {
        driver: stateDriver,
        vehicle: stateVehicle,
        vehicleState: stateVehicleState,
      },
    })
  }

  // ========== 計算差異與耗油量 ==========
  const calculateDifference = () => {
    if (!startVehicleState) return null

    // 如果沒有輸入值，返回 null 表示尚未輸入
    if (!fuelLevel || !mileage) {
      return {
        fuelDiff: null,
        mileageDiff: null,
        fuelConsumed: null,
        remainingFuel: null,
        avgConsumption: null,
      }
    }

    const currentFuel = parseFloat(fuelLevel)
    const currentMileage = parseFloat(mileage)
    const addedFuel = parseFloat(fuelAdded) || 0 // 本日加油量，預設為 0

    // 如果數值無效，返回 null
    if (isNaN(currentFuel) || isNaN(currentMileage)) {
      return {
        fuelDiff: null,
        mileageDiff: null,
        fuelConsumed: null,
        remainingFuel: null,
        avgConsumption: null,
      }
    }

    const startFuel = startVehicleState.fuelLevel ?? 0
    const startMileage = startVehicleState.mileage ?? 0
    const fuelDiff = currentFuel - startFuel
    const mileageDiff = currentMileage - startMileage

    // 計算實際耗油量：接班油量 + 加油量 - 交班油量
    const fuelConsumed = startFuel + addedFuel - currentFuel

    // 剩餘油量就是交班油量
    const remainingFuel = currentFuel

    // 計算平均耗油量（km/L）：行駛里程 / 耗油量
    const avgConsumption = fuelConsumed > 0 ? mileageDiff / fuelConsumed : null

    return {
      fuelDiff,
      mileageDiff,
      fuelConsumed,
      remainingFuel,
      avgConsumption,
    }
  }

  const diff = calculateDifference()

  // ========== 載入中 ==========
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#6B705C' }} />
        <Typography variant="body1" sx={{ mt: 2, color: '#6B705C' }}>
          載入中...
        </Typography>
      </Box>
    )
  }

  // ========== Render ==========
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        py: 3,
      }}
    >
      <Container maxWidth="sm">
        {/* 返回按鈕 */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            mb: 1.5,
            color: '#6B705C',
            '&:hover': {
              backgroundColor: 'rgba(107, 112, 92, 0.08)',
            },
          }}
        >
          返回任務列表
        </Button>

        {/* 頁面標題 */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 1.5,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #6B705C 0%, #A5A58D 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DirectionsCarIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                車輛交班
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
                {vehicle?.vehicleNo} - {vehicle?.vehicleTypeName}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 錯誤對話框 */}
        <Dialog
          open={!!error}
          onClose={() => setError(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
            <ErrorOutlineIcon />
            錯誤
          </DialogTitle>
          <DialogContent>
            {error?.split('\n').map((line, i) =>
              i === 0 ? (
                <Typography key={i}>{line}</Typography>
              ) : (
                <Typography key={i} variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  {line}
                </Typography>
              )
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setError(null)} variant="contained" color="primary">
              確定
            </Button>
          </DialogActions>
        </Dialog>

        {/* 成功對話框 */}
        <Dialog
          open={successDialogOpen}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              textAlign: 'center',
            },
          }}
        >
          <DialogContent sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50' }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#6B705C' }}>
                交班成功！
              </Typography>
              <Typography variant="body1" color="text.secondary">
                系統將在 {countdown} 秒後自動跳轉至日報表頁面
              </Typography>
            </Box>
          </DialogContent>
          {/* 確認：立即跳轉日報表（提前完成倒數後動作） */}
          <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 0 }}>
            <Button
              onClick={goToDailyReport}
              variant="contained"
              sx={{ minWidth: 140, fontWeight: 700, bgcolor: '#4caf50', '&:hover': { bgcolor: '#43a047' } }}
            >
              確認
            </Button>
          </DialogActions>
        </Dialog>

        {/* 無班可交對話框 */}
        <Dialog
          open={noDutyDialogOpen}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              textAlign: 'center',
            },
          }}
        >
          <DialogContent sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <ErrorOutlineIcon sx={{ fontSize: 80, color: '#ff9800' }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#6B705C' }}>
                您目前沒有進行中的班次
              </Typography>
              <Typography variant="body1" color="text.secondary">
                請先接班後再進行交班作業
              </Typography>
              <Typography variant="body2" color="text.secondary">
                系統將在 {noDutyCountdown} 秒後自動返回首頁
              </Typography>
            </Box>
          </DialogContent>
          {/* 確認：立即返回首頁 */}
          <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 0 }}>
            <Button
              onClick={goToHome}
              variant="contained"
              sx={{ minWidth: 140, fontWeight: 700, bgcolor: '#ff9800', '&:hover': { bgcolor: '#fb8c00' } }}
            >
              確認
            </Button>
          </DialogActions>
        </Dialog>

        {/* 任務阻擋交班對話框 */}
        <Dialog
          open={taskBlockedDialogOpen}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              textAlign: 'center',
            },
          }}
        >
          <DialogContent sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <ErrorOutlineIcon sx={{ fontSize: 80, color: '#f44336' }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#6B705C' }}>
                無法交班
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {taskBlockedMessage || '有未完成的任務，請先處理後再交班'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                系統將在 {taskBlockedCountdown} 秒後自動返回任務列表
              </Typography>
            </Box>
          </DialogContent>
          {/* 確認：立即返回任務列表 */}
          <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 0 }}>
            <Button
              onClick={goToTaskList}
              variant="contained"
              sx={{ minWidth: 140, fontWeight: 700, bgcolor: '#f44336', '&:hover': { bgcolor: '#e53935' } }}
            >
              確認
            </Button>
          </DialogActions>
        </Dialog>

        {/* 接班資訊（非驥達的車不顯示油量／里程） */}
        {fuelMileageRequired && startVehicleState && (
          <Paper elevation={2} sx={{ p: 2, mb: 1.5, borderRadius: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
              接班資訊
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    <SpeedIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                    接班里程
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(startVehicleState.mileage ?? 0).toLocaleString()} km
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    <LocalGasStationIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                    接班油量
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(startVehicleState.fuelLevel ?? 0).toFixed(1)} 公升
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* 交班狀態輸入表單（非驥達的車不填油量／里程，整段不顯示） */}
        {fuelMileageRequired && (
        <Paper elevation={3} sx={{ p: 2, mb: 1.5, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '1.25rem', color: '#6B705C' }}>
            交班車輛狀態
          </Typography>

          {/* 里程 */}
          <TextField
            fullWidth
            label="交班里程"
            type="number"
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
            disabled={submitting}
            sx={{ mb: 2 }}
            InputLabelProps={{
              sx: { fontSize: '1.5rem', fontWeight: 700 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SpeedIcon sx={{ color: '#6B705C' }} />
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">公里</InputAdornment>,
            }}
            inputProps={{
              min: 0,
              step: 1,
            }}
          />

          {/* 油量 */}
          <TextField
            fullWidth
            label="交班油量"
            type="number"
            value={fuelLevel}
            onChange={(e) => setFuelLevel(e.target.value)}
            disabled={submitting}
            sx={{ mb: 2 }}
            InputLabelProps={{
              sx: { fontSize: '1.5rem', fontWeight: 700 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocalGasStationIcon sx={{ color: '#6B705C' }} />
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">公升</InputAdornment>,
            }}
            inputProps={{
              min: 0,
              max: 500,
              step: 0.1,
            }}
          />

          {/* 本日加油量 */}
          <TextField
            fullWidth
            label="本日加油量"
            type="number"
            value={fuelAdded}
            onChange={(e) => setFuelAdded(e.target.value)}
            disabled={submitting}
            placeholder="若無加油請留空"
            InputLabelProps={{
              sx: { fontSize: '1.5rem', fontWeight: 700 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocalGasStationIcon sx={{ color: '#4caf50' }} />
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">公升</InputAdornment>,
            }}
            inputProps={{
              min: 0,
              max: 500,
              step: 0.1,
            }}
          />

          {/* 統計資訊顯示 */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            本日統計
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  行駛里程
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color:
                      diff?.mileageDiff !== null && diff?.mileageDiff !== undefined
                        ? '#6B705C'
                        : 'text.secondary',
                  }}
                >
                  {diff?.mileageDiff !== null && diff?.mileageDiff !== undefined
                    ? `${diff.mileageDiff.toLocaleString()} km`
                    : '--'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  耗油量
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color:
                      diff?.fuelConsumed !== null && diff?.fuelConsumed !== undefined
                        ? '#f44336'
                        : 'text.secondary',
                  }}
                >
                  {diff?.fuelConsumed !== null && diff?.fuelConsumed !== undefined
                    ? `${diff.fuelConsumed.toFixed(1)} 公升`
                    : '--'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  剩餘油量
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color:
                      diff?.remainingFuel !== null && diff?.remainingFuel !== undefined
                        ? '#2196f3'
                        : 'text.secondary',
                  }}
                >
                  {diff?.remainingFuel !== null && diff?.remainingFuel !== undefined
                    ? `${diff.remainingFuel.toFixed(1)} 公升`
                    : '--'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  平均耗油量
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color:
                      diff?.avgConsumption !== null && diff?.avgConsumption !== undefined
                        ? '#4caf50'
                        : 'text.secondary',
                  }}
                >
                  {diff?.avgConsumption !== null && diff?.avgConsumption !== undefined
                    ? `${diff.avgConsumption.toFixed(2)} km/L`
                    : '--'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* 提示 */}
          <Box
            sx={{
              p: 1.5,
              backgroundColor: '#fff3cd',
              borderRadius: 2,
              border: '1px solid #ffc107',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
              📝 注意事項
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              • 請確認油量與里程數正確無誤
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              • 交班後將結束本次執勤
            </Typography>
          </Box>
        </Paper>
        )}

        {/* 底部按鈕的佔位空間 */}
        <Box sx={{ height: 80 }} />
      </Container>

      {/* 確認交班按鈕 - 固定在底部 */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          backgroundColor: 'rgba(255, 232, 214, 0.95)',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Container maxWidth="sm">
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            onClick={handleConfirm}
            disabled={submitting || !driver || !vehicle}
            sx={{
              backgroundColor: '#6B705C',
              color: 'white',
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 700,
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(107, 112, 92, 0.3)',
              '&:hover': {
                backgroundColor: '#5A5F4A',
                boxShadow: '0 6px 16px rgba(107, 112, 92, 0.4)',
              },
              '&:disabled': {
                backgroundColor: '#A5A58D',
              },
            }}
          >
            {submitting ? '處理中...' : !vehicle ? '請先接班後再交班' : '確認交班'}
          </Button>
        </Container>
      </Box>
    </Box>
  )
}

export default VehicleHandoverPage
